import React, { useEffect, useState, useRef, useContext, useMemo } from "react";
import * as tt from "@tomtom-international/web-sdk-maps";
// import * as ttapi from "@tomtom-international/web-sdk-services";
import "../css/tomtom.scss";
import "@tomtom-international/web-sdk-maps/dist/maps.css";
import { useLocation } from "react-router-dom";
import { HOME_LAT, HOME_LNG, statusMap } from "../popData";
import Footer from "./Footer";
import {
  geoCodeAddresses,
  getPrimariesMetadata,
  setupPopUpHtml,
  formatAddressQueries,
  getInvalidAddressQueries,
} from "../helperFunctions/mapHelpers";
import { useShiftControl } from "../customHooks";
import { MapLocationsList, MapDistanceAndTimeUI } from ".";
import {
  JobsheetsContext,
  QuotesContext,
  TimesheetsContext,
} from "../providers";
import {
  filterSchedulerPrimaries,
  getPrimariesBetween,
  getWeekBookends,
} from "../helperFunctions/calendarFunctions";
import CalendarControls from "./CalendarControls";
import { dateTimeFormatter } from "../helperFunctions/dateFormatters";
import { addDays, endOfDay, startOfDay } from "date-fns";

let tomtomKey = import.meta.env.VITE_TOM_TOM;

if (import.meta.env.MODE === "production") {
  tomtomKey = "bIAp2eyChQynneWzWbRRCzgdeeGG3cRj";
}

/* >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>> 

Instead of following flow:
- map = call_api
- all primaries
- filteredData = apply_filters()
  -> locationData = call_api(filteredData)
    -> markers = call_tt_method(locationData)
      - apply markers

This is inefficient because we are calling the tomtom api several times when we
have already done this before. So new scheme will be.

- all primaries (on load)
- all locationData (on load) call_api(primaries)
  - attach markers (on load) call_tt_method(locationData)

- filteredData = apply filters(locationData)
- removeAllMarkers(locationData), 
- addMarkers(filteredData)  




<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<< */
const timePeriods = ["All", "Week", "Date"];

const TomTomMap = () => {
  const location = useLocation();

  // Bring in primaries relevant to scheduling
  const { timesheets } = useContext(TimesheetsContext);
  const { timesheets: jobsheets } = useContext(JobsheetsContext);
  const { timesheets: quotes } = useContext(QuotesContext);

  const primaries = useMemo(() => {
    return filterSchedulerPrimaries(
      [timesheets, jobsheets, quotes],
      location?.state?.sheets
    );
  }, [jobsheets, quotes, timesheets]);

  // Allows me to determine length of each status section and if any primaries of that
  // status were passed in.
  const primariesMetadata = useMemo(() => {
    return getPrimariesMetadata(primaries);
  }, [primaries]);

  // Map state
  const mapElement = useRef();
  const [map, setMap] = useState({});
  const [mapLoaded, setMapLoaded] = useState(false);

  // Filtered data with lon_lat positions attached
  const [locationData, setLocationData] = useState([]);

  const [badAddresses, setBadAddresses] = useState([]);

  // Base date for time filters
  const [showDate, setShowDate] = useState(() => new Date());

  // Options to apply to filter primaries
  const [options, setOptions] = useState({
    statusFilters: ["booked-in", "arrange-date", "provisional", "selected"],
    timePeriodIndex: 0,
  });

  // Filtered data contains what primaries you want to appear on map.
  const [filteredData, setFilteredData] = useState([]);

  // Enables user to listen for shift key
  const [shiftDown] = useShiftControl();

  // Map markers, each marker represents item in locationData
  // const [markers, setMarkers] = useState([]);

  // Route control state
  const [routePoints, setRoutePoints] = useState({
    r1: null,
    r2: null,
  });

  // Apply filters to locationData every time options change.
  useEffect(() => {
    const newData = applyFilters(locationData);
    setFilteredData(newData);
  }, [options, locationData, showDate]);

  useEffect(() => {
    // Draw markers based on filtered data

    if (mapLoaded) {
      removeAllMarkers();
      addMarkers();
    }
  }, [filteredData, mapLoaded]);

  function removeAllMarkers() {
    locationData.forEach((location) => {
      location.marker.remove();
    });
  }

  function addMarkers() {
    // Add home marker to map
    const home = locationData.find((location) => location.uid === "home");
    if (home) {
      home.marker.addTo(map);
    }

    // Add filtered data markers to map
    filteredData.forEach((location) => location.marker.addTo(map));
  }

  // Attach lon,lat positions and markers to primaries and set as state => locationData
  useEffect(() => {
    const addressQueries = formatAddressQueries(primaries);
    const invalidAddresses = getInvalidAddressQueries(
      primaries,
      addressQueries
    );

    geoCodeAddresses(
      primaries,
      addressQueries,
      setLocationData,
      setRoutes,
      tomtomKey
    );

    setBadAddresses(invalidAddresses);
  }, [primaries]);

  // console.log("location data", locationData);

  // Load map and toggle map loaded to true
  useEffect(() => {
    let map = tt.map({
      key: tomtomKey,
      container: mapElement.current,
      center: [HOME_LNG, HOME_LAT],
      boxZoom: false,
      zoom: 10,

      stylesVisibility: {
        trafficIncidents: false,
        trafficFlow: false,
        pointsOfInterest: false,
      },
    });

    setMap(map);

    map.on("load", () => {
      // map.setFilter("admin", ["==", "iso_3166_1", "US"]);
      setMapLoaded(true);
    });

    return () => map.remove();
  }, []);

  function applyFilters(data) {
    // Date filters
    let timeFiltered = data;
    let passThroughs = data.filter((p) => {
      if (p.status === "selected" || p.status === "arrange-date") {
        return true;
      }
    });
    switch (options.timePeriodIndex) {
      case 0: {
        // don't filter
        break;
      }
      case 1: {
        let [date1, date2] = getWeekBookends(showDate);
        timeFiltered = getPrimariesBetween(data, date1, date2);
        timeFiltered = timeFiltered.concat(passThroughs);
        break;
      }
      case 2: {
        // let [date1, date2] = getWeekBookends(showDate);
        let dayStart = startOfDay(showDate);
        let dayEnd = endOfDay(showDate);

        timeFiltered = getPrimariesBetween(data, dayStart, dayEnd);
        timeFiltered = timeFiltered.concat(passThroughs);
        break;
      }
      default: {
        break;
      }
    }

    // Show filters
    const filteredLocations = timeFiltered.filter((ts) => {
      return options.statusFilters.includes(ts.status);
    });

    return filteredLocations;
  }

  function cycleTimePeriods(e) {
    // cycle through time periods filter on button click
    setRoutePoints([null, null]);
    setOptions((prevState) => {
      let newIndex = (prevState.timePeriodIndex + 1) % timePeriods.length;
      return {
        ...prevState,
        timePeriodIndex: newIndex,
      };
    });
  }

  function setRoutes(location) {
    setRoutePoints((prevState) => {
      if (prevState?.r1 && !prevState?.r2) {
        // Only r1 active. Set second point.
        return { ...prevState, r2: { ...location } };
      } else if (prevState?.r1 && prevState?.r2) {
        // r1 && r2 active. Set first point.
        return { r2: null, r1: { ...location } };
      } else {
        // No points active, set first point
        return { ...prevState, r1: { ...location } };
      }
    });
  }

  function handleDateChange(e) {
    setShowDate(e.target.valueAsDate);
  }

  // function markerClick(e, location, popup, marker) {
  //   e.preventDefault();
  //   if (e.shiftKey) {
  //     if (!popup.isOpen()) {
  //       marker.togglePopup();
  //     }

  //     setRoutes(location);
  //   } else {
  //     // logic to show who it is.
  //   }
  // }

  return (
    <div className="map-page">
      <div className="tomtom-map-container">
        <div className="map-ui-header">
          {/* Time Filters >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>> */}
          <div className="time-filters">
            <div className="time-period-ui">
              {options.timePeriodIndex === 0 && (
                <input type="date" key="all" disabled={true} />
              )}

              {options.timePeriodIndex === 1 && (
                <CalendarControls
                  showDate={showDate}
                  setShowDate={setShowDate}
                />
              )}

              {options.timePeriodIndex === 2 && (
                <input
                  key="day"
                  type="date"
                  value={dateTimeFormatter(showDate, "d_full_variant")}
                  onChange={handleDateChange}
                />
              )}
            </div>
            <button className="time-period-control" onClick={cycleTimePeriods}>
              {timePeriods[options.timePeriodIndex]}
            </button>
          </div>
          {/* Time Filters <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<< */}
          {/* UI Showing Distance & Time >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>> */}
          <MapDistanceAndTimeUI
            map={map}
            setMap={setMap}
            routePoints={routePoints}
            setRoutePoints={setRoutePoints}
            shiftDown={shiftDown}
            // options={options}
            tomtomKey={tomtomKey}
          />
          {/* UI Showing Distance & Time <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<< */}
        </div>

        <div className="locations-list-and-map">
          {/* List of Map Locations >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>> */}
          <MapLocationsList
            metadata={primariesMetadata}
            locationData={filteredData}
            setRoutes={setRoutes}
            options={options}
            setOptions={setOptions}
            badAddresses={badAddresses}
          />
          {/* List of Map Locations <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<< */}

          {/* Map Element >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>> */}
          {map && <div ref={mapElement} className="tt-map"></div>}
          {/* Map Element <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<< */}
        </div>
      </div>
      <Footer back={true} title="Map" backTo={"/"}></Footer>
    </div>
  );
};

export default TomTomMap;

// const addressArray = [
//   {
//     uid: "dnlwnln",
//     authorisedBy: "Paul Henderson",
//     flat: "2/2",
//     address: "1162 Tollcross Road",
//     postcode: "g32 8he",
//     status: "booked-in",
//     position: { lng: -4.16979, lat: 55.84309 },
//   },
//   {
//     uid: "kllkldkld",
//     authorisedBy: "Aloysius Saint",
//     flat: "",
//     address: "45 Hill Street",
//     postcode: "G36RJ",
//     status: "arrange-date",
//     position: { lng: -4.26411, lat: 55.86701 },
//   },
//   {
//     uid: "knianlkndl",
//     authorisedBy: "Milly Gavie",
//     flat: "",
//     address: "1 Main St, Great North Road",
//     postcode: "g62 6bh",
//     status: "unresolved",
//     position: { lng: -4.31689, lat: 55.93608 },
//   },
// ];

// const HOME_LNG = -4.1485902;
// const HOME_LAT = 55.816631;

// const origin = {
//   lng: HOME_LNG,
//   lat: HOME_LAT,
// };

// for (let item of addressArray) {
//   console.log("In ", item.postcode);
//   function callbackFn(result) {
//     console.log("For ", item.postcode);
//     console.log("Result", result);
//   }

//   ttapi.services
//     .geocode({
//       key: import.meta.env.VITE_TOM_TOM,
//       bestResult: true,
//       center: [HOME_LNG, HOME_LAT],
//       radius: 100000,
//       query: item.postcode,
//     })
//     .then(callbackFn);
// }

// Set map bounds
// let sw = new tt.LngLat(-4.450295660690394, 55.79268072715289);
// let ne = new tt.LngLat(-4.0511558904865, 55.908149771385844);
// let bounds = new tt.LngLatBounds([sw, ne]);
// map.setMaxBounds(bounds);

// // Distance Matrix
// const pointsForDestinations = locations.map(() => {

// })
// const callParameters = {
//   key: import.meta.env.VITE_TOM_TOM,
//   destinations: pointsForDestinations,
//   origins: [convertToPoints(origin)],
// };
// return new Promise((resolve, reject) => {
//   ttapi.services.matrixRouting(callParameters);
// });
